import React, { useCallback, useMemo, useState } from "react";

import { PlusCircleOutlined } from "@ant-design/icons";
import { Button } from "antd";
import debounce from "lodash/debounce";
import Highlighter from "react-highlight-words";

import NoResults from "../../../Common/NoResultsGIF";
import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import SupplyPickupSearchInput from "../../Common/SupplyPickupSearchInput";
import InventoryCardStepper from "./InventoryCardStepper";

import pumpkinGIF from "../../../../assets/images/environment/pumpkin.gif";
import "./InventoryPanel.css";

const InventoryPanel = ({
  loading,
  loadingEditingData,
  inventoryItems,
  requestedItemsMap,
  updateQuantity,
  addItem,
  fetchInventoryItems,
  pageTitle = "",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const highlightWords = useMemo(
    () => searchTerm.trim().split(/\s+/).filter(Boolean),
    [searchTerm],
  );
  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      fetchInventoryItems(value);
    }, 500),
    [setSearchTerm],
  );

  return (
    <div className="InventoryWrapper">
      {pageTitle && (
        <div className="InventoryPanel__SubHeader">
          <div className="InventoryPanel__SubHeaderAccent" aria-hidden />
          <span className="InventoryPanel__SubHeaderTitle">{pageTitle}</span>
        </div>
      )}
      <div className="InventoryPanel__SearchRow">
        <SupplyPickupSearchInput
          value={searchTerm}
          placeholder="Search Items"
          disabled={loadingEditingData}
          onChange={(value) => {
            setSearchTerm(value);
            debouncedSetSearchTerm(value);
          }}
          onSearch={(value) => {
            setSearchTerm(value);
            debouncedSetSearchTerm.cancel();
            fetchInventoryItems(value);
          }}
        />
      </div>

      <StyledScrollbar className="SupplyInventoryItemsScrollbar">
        <div className="InventoryList">
          {!loading && inventoryItems.length > 0 && inventoryItems.map((item) => (
            <div
              key={item.inventory_item_id}
              className="InventoryCard"
            >
              <div className="InventoryCard__ImageAndLabel">
                <div className="image-container">
                  {item.image_url && <img src={item.image_url} alt={item.item_name} loading="lazy" />}
                  {!item.image_url && <span>No Image</span>}
                </div>
                <StyledTooltip title={item.item_name} showArrow={false}>
                  <span className="item-name">
                    {highlightWords.length > 0 ? (
                      <Highlighter
                        highlightClassName="InventoryCard__SearchHighlight"
                        searchWords={highlightWords}
                        autoEscape
                        textToHighlight={item.item_name}
                      />
                    ) : (
                      item.item_name
                    )}
                  </span>
                </StyledTooltip>
              </div>
              <div className="InventoryCardButtonDiv">
                {requestedItemsMap.has(item.inventory_item_id) ? (
                  <InventoryCardStepper
                    min={0}
                    value={requestedItemsMap.get(item.inventory_item_id).quantity}
                    onChange={(qty) => updateQuantity(item.inventory_item_id, qty)}
                  />
                ) : (
                  <Button
                    type="default"
                    className="add"
                    onClick={() => addItem(item)}
                    icon={<PlusCircleOutlined className="add-icon" aria-hidden />}
                    iconPosition="end"
                  >
                    Add
                  </Button>
                )}
              </div>
            </div>
          ))}
          {!loading && inventoryItems.length === 0 && (
          <NoResults
            className="SelectLocationForm__NoResult"
            image={pumpkinGIF}
            message="No Result Found"
          />
          )}
          {loading && (
          <div
            className="InventoryCard"
          >
            <div className="InventoryCard__ImageAndLabel">
              <div className="image-container ActiveloadingAnimation" />
            </div>
          </div>
          )}
        </div>
      </StyledScrollbar>
    </div>
  );
};

export default InventoryPanel;
